/**
 * Inspections Data Layer - TanStack Query Hooks
 * For Unit 7: Inspections Page Modernization
 */

'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from './use-toast';

// ============================================================================
// TYPES
// ============================================================================

export interface InspectionsQueryFilters {
  search?: string | undefined;
  status?: 'all' | 'pending' | 'in_progress' | 'completed' | 'approved' | 'rejected' | undefined;
  inspector?: string | undefined;
  dateRange?: 'today' | 'this-week' | 'this-month' | 'last-30-days' | 'last-90-days' | 'custom' | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

export interface InspectionCustomer {
  id: string;
  name: string;
}

export interface InspectionJob {
  id: string;
  jobNumber: string;
  customer: InspectionCustomer;
}

export interface InspectionVehicle {
  make: string;
  model: string;
  year: number;
  vin: string;
}

export interface InspectionAssignee {
  id: string;
  name: string;
  avatar?: string;
}

export interface Inspection {
  id: string;
  inspectionNumber: string;
  job: InspectionJob;
  vehicle: InspectionVehicle;
  status: 'pending' | 'in_progress' | 'completed' | 'approved' | 'rejected';
  assignedTo?: InspectionAssignee;
  inspectionDate: string;
  photosCount: number;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InspectionsResponse {
  inspections: Inspection[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UpdateInspectionData {
  status?: 'pending' | 'in_progress' | 'completed' | 'approved' | 'rejected';
  assignedTo?: InspectionAssignee;
  inspectionDate?: string;
  photosCount?: number;
}

export interface RejectInspectionData {
  reason: string;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

const inspectionsKeys = {
  all: ['inspections'] as const,
  lists: () => [...inspectionsKeys.all, 'list'] as const,
  list: (filters: InspectionsQueryFilters) => [...inspectionsKeys.lists(), filters] as const,
  details: () => [...inspectionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...inspectionsKeys.details(), id] as const,
};

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function fetchInspections(filters: InspectionsQueryFilters = {}): Promise<InspectionsResponse> {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.status && filters.status !== 'all') params.append('status', filters.status);
  if (filters.inspector) params.append('inspector', filters.inspector);
  if (filters.dateRange) params.append('dateRange', filters.dateRange);
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.append('dateTo', filters.dateTo);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.pageSize) params.append('pageSize', String(filters.pageSize));

  const url = `/api/inspections${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error('Failed to fetch inspections');
  }

  return response.json();
}

async function fetchInspection(id: string): Promise<Inspection> {
  const response = await fetch(`/api/inspections/${id}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Inspection not found');
    }
    throw new Error('Failed to fetch inspection');
  }

  return response.json();
}

async function updateInspection(id: string, data: UpdateInspectionData): Promise<Inspection> {
  const response = await fetch(`/api/inspections/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to update inspection');
  }

  return response.json();
}

async function deleteInspection(id: string): Promise<void> {
  const response = await fetch(`/api/inspections/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete inspection');
  }
}

async function approveInspection(id: string): Promise<Inspection> {
  const response = await fetch(`/api/inspections/${id}/approve`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to approve inspection');
  }

  return response.json();
}

async function rejectInspection(id: string, data: RejectInspectionData): Promise<Inspection> {
  const response = await fetch(`/api/inspections/${id}/reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject inspection');
  }

  return response.json();
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * useInspections - Fetch inspections list with filters and pagination
 *
 * @param filters - Filter criteria (search, status, inspector, dateRange, page, pageSize)
 * @returns Query result with inspections data, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useInspections({
 *   search: 'INS-2024',
 *   status: 'pending',
 *   inspector: 'user-001',
 *   dateRange: 'last-30-days',
 *   page: 1,
 *   pageSize: 20
 * });
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 *
 * return (
 *   <div>
 *     {data.inspections.map(inspection => (
 *       <InspectionCard key={inspection.id} inspection={inspection} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useInspections(filters: InspectionsQueryFilters = {}) {
  return useQuery({
    queryKey: inspectionsKeys.list(filters),
    queryFn: () => fetchInspections(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes - inspections data changes frequently
    refetchOnWindowFocus: true,
  });
}

/**
 * useInspection - Fetch single inspection by ID
 *
 * @param id - Inspection ID
 * @returns Query result with inspection data, loading state, and error
 *
 * @example
 * ```tsx
 * const { data: inspection, isLoading, error } = useInspection('ins-001');
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 *
 * return (
 *   <div>
 *     <h1>{inspection.inspectionNumber}</h1>
 *     <p>Status: {inspection.status}</p>
 *     <p>Photos: {inspection.photosCount}</p>
 *   </div>
 * );
 * ```
 */
export function useInspection(id: string | undefined) {
  return useQuery({
    queryKey: inspectionsKeys.detail(id || ''),
    queryFn: () => fetchInspection(id!),
    enabled: !!id, // Only fetch when id is provided
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });
}

/**
 * useUpdateInspection - Update inspection mutation
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const updateInspection = useUpdateInspection();
 *
 * const handleUpdate = async (inspectionId: string) => {
 *   await updateInspection.mutateAsync({
 *     id: inspectionId,
 *     data: {
 *       status: 'in_progress',
 *       assignedTo: { id: 'user-001', name: 'Jane Smith' }
 *     }
 *   });
 * };
 *
 * return (
 *   <button
 *     onClick={() => handleUpdate('ins-001')}
 *     disabled={updateInspection.isPending}
 *   >
 *     {updateInspection.isPending ? 'Updating...' : 'Start Inspection'}
 *   </button>
 * );
 * ```
 */
export function useUpdateInspection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInspectionData }) =>
      updateInspection(id, data),
    onSuccess: (updatedInspection) => {
      // Update the inspection in cache
      queryClient.setQueryData(
        inspectionsKeys.detail(updatedInspection.id),
        updatedInspection
      );
      // Invalidate inspections list to reflect changes
      queryClient.invalidateQueries({ queryKey: inspectionsKeys.lists() });

      toast({
        title: 'Success',
        description: 'Inspection updated successfully',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: (error instanceof Error ? error.message : String(error)) || 'Failed to update inspection',
        variant: 'error',
      });
    },
  });
}

/**
 * useDeleteInspection - Delete inspection mutation
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const deleteInspection = useDeleteInspection();
 *
 * const handleDelete = async (inspectionId: string) => {
 *   if (confirm('Are you sure you want to delete this inspection?')) {
 *     await deleteInspection.mutateAsync(inspectionId);
 *   }
 * };
 *
 * return (
 *   <button
 *     onClick={() => handleDelete('ins-001')}
 *     disabled={deleteInspection.isPending}
 *   >
 *     {deleteInspection.isPending ? 'Deleting...' : 'Delete'}
 *   </button>
 * );
 * ```
 */
export function useDeleteInspection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: deleteInspection,
    onSuccess: () => {
      // Invalidate all inspections queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: inspectionsKeys.lists() });

      toast({
        title: 'Success',
        description: 'Inspection deleted successfully',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: (error instanceof Error ? error.message : String(error)) || 'Failed to delete inspection',
        variant: 'error',
      });
    },
  });
}

/**
 * useApproveInspection - Approve inspection mutation
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const approveInspection = useApproveInspection();
 *
 * const handleApprove = async (inspectionId: string) => {
 *   await approveInspection.mutateAsync(inspectionId);
 * };
 *
 * return (
 *   <button
 *     onClick={() => handleApprove('ins-001')}
 *     disabled={approveInspection.isPending}
 *   >
 *     {approveInspection.isPending ? 'Approving...' : 'Approve'}
 *   </button>
 * );
 * ```
 */
export function useApproveInspection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: approveInspection,
    onSuccess: (updatedInspection) => {
      // Update the inspection in cache
      queryClient.setQueryData(
        inspectionsKeys.detail(updatedInspection.id),
        updatedInspection
      );
      // Invalidate inspections queries to reflect status change
      queryClient.invalidateQueries({ queryKey: inspectionsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: inspectionsKeys.detail(updatedInspection.id),
      });

      toast({
        title: 'Success',
        description: 'Inspection approved successfully',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: (error instanceof Error ? error.message : String(error)) || 'Failed to approve inspection',
        variant: 'error',
      });
    },
  });
}

/**
 * useRejectInspection - Reject inspection mutation with reason
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const rejectInspection = useRejectInspection();
 *
 * const handleReject = async (inspectionId: string) => {
 *   const reason = prompt('Enter rejection reason:');
 *   if (reason) {
 *     await rejectInspection.mutateAsync({
 *       id: inspectionId,
 *       reason
 *     });
 *   }
 * };
 *
 * return (
 *   <button
 *     onClick={() => handleReject('ins-001')}
 *     disabled={rejectInspection.isPending}
 *   >
 *     {rejectInspection.isPending ? 'Rejecting...' : 'Reject'}
 *   </button>
 * );
 * ```
 */
export function useRejectInspection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectInspection(id, { reason }),
    onSuccess: (updatedInspection) => {
      // Update the inspection in cache
      queryClient.setQueryData(
        inspectionsKeys.detail(updatedInspection.id),
        updatedInspection
      );
      // Invalidate inspections queries to reflect status change
      queryClient.invalidateQueries({ queryKey: inspectionsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: inspectionsKeys.detail(updatedInspection.id),
      });

      toast({
        title: 'Success',
        description: 'Inspection rejected',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: (error instanceof Error ? error.message : String(error)) || 'Failed to reject inspection',
        variant: 'error',
      });
    },
  });
}
